import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminStore } from '../../stores/adminStore';
import { useTranslation } from '../../hooks/useTranslation';
import { toLocalizedFormat, UNIVERSAL_DATE_FORMAT } from '@dutchinvoice/shared-utils';
import { useInvoiceStore } from '../../stores/invoiceStore';
import { format } from 'date-fns';
import { LocalizedLink } from '../../components/LocalizedLink';

export const UserCompanies: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { uid } = useParams<{ uid: string }>();
  const language = useInvoiceStore(state => state.settings.language);
  const {
    selectedUser,
    userCompanies,
    userCompaniesLoading,
    userCompaniesError,
    fetchUserDetails,
    fetchUserCompanies
  } = useAdminStore();

  useEffect(() => {
    if (!uid) {
      navigate('/admin');
      return;
    }
    
    fetchUserDetails(uid);
    fetchUserCompanies(uid);
  }, [uid, navigate, fetchUserDetails, fetchUserCompanies]);

  if (!selectedUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50/50 backdrop-blur-xl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Ensure selectedUser has settings to prevent errors
  if (!selectedUser.settings) {
    console.log('[UserCompanies] selectedUser.settings is missing, adding empty object');
    selectedUser.settings = {
      language: 'en',
      country: 'NL',
      invoiceStartNumber: 1,
      latestInvoiceNumber: 0,
      invoiceNumberFormat: {
        prefix: 'INV',
        includeYear: true,
        yearFormat: 'full',
        separator: '-',
        numberPadding: 4
      }
    };
  }

  // Convert timestamp to date string in universal format
  const createdAtDate = format(new Date(selectedUser.createdAt), UNIVERSAL_DATE_FORMAT);

  return (
    <div className="min-h-screen bg-gray-50/50 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <LocalizedLink
              to="/admin"
              className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('admin.backToUsers')}
            </LocalizedLink>
            <h1 className="text-2xl font-semibold text-gray-900">
              {t('admin.userCompanies.title', { email: selectedUser.email })}
            </h1>
          </div>
          <div className="flex space-x-3">
            <LocalizedLink
              to={`/admin/user/${uid}/invoices`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('admin.users.viewInvoices')}
            </LocalizedLink>
            <LocalizedLink
              to={`/admin/user/${uid}/settings`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('admin.users.viewSettings')}
            </LocalizedLink>
          </div>
        </div>

        {/* User Info Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">{t('admin.users.email')}</div>
              <div className="text-gray-900">{selectedUser.email}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">{t('admin.users.company')}</div>
              <div className="text-gray-900">{selectedUser.defaultCompany || '-'}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">{t('admin.users.joined')}</div>
              <div className="text-gray-900">{toLocalizedFormat(createdAtDate, language)}</div>
            </div>
          </div>
        </div>

        {/* Companies Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="text-lg font-medium text-gray-900">
              {t('admin.userCompanies.companies')} ({userCompanies.length})
            </h2>
          </div>
          
          {userCompaniesLoading && userCompanies.length === 0 ? (
            <div className="p-6 text-center text-gray-500">{t('common.loading')}</div>
          ) : userCompaniesError ? (
            <div className="p-6 text-center text-red-500">{userCompaniesError}</div>
          ) : userCompanies.length === 0 ? (
            <div className="p-6 text-center text-gray-500">{t('admin.userCompanies.noCompanies')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('company.name')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('company.address')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('company.city')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('company.contactName')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      e-mail
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('company.createdAt')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {userCompanies.map((company) => {
                    const companyCreatedAt = format(new Date(company.createdAt * 1000), UNIVERSAL_DATE_FORMAT);
                    
                    return (
                      <tr key={company.id} className="hover:bg-gray-50/50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {company.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {company.address}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {company.city}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {company.contactName || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {company.email || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {toLocalizedFormat(companyCreatedAt, language)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 